import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import { Route, Switch, useRouteMatch, match as Match } from 'react-router-dom';
import { Intl } from '@erecruit/core';
import { TabLayout } from '@erecruit/modules';
import { Container, Flex, FlexItem, H1, H4, Modal } from '@erecruit/ui-kit';
import { AppPermissions } from '@erecruit/types';
import { InsightSpinner } from '../../App/components/InsightPortal';

import {
  PositionDetails,
  MatchStatuses,
  PositionHeader,
  PositionNotes,
} from './components';

import { MATCH_STATUSES } from './helpers';
import {
  closePositionRecord,
  fetchPositionRecordInfo,
  fetchMatchStatus,
  selectors,
} from './state/reducer';
import { RootState } from '../../redux/reducers';
import {
  PositionRecordProps,
  PositionRecordParams,
  MatchParams,
} from './types';

const { FormattedMessage } = Intl;

export const PositionRecord = ({
  closePositionRecord,
  fetchPositionRecordInfo,
  isClosingPosition,
  isInsight,
  isLoadingInfo,
  infoSections,
}: PositionRecordProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [closeModalVisible, setCloseModalVisible] = useState(false);

  const routeMatch = useRouteMatch<PositionRecordParams>();
  const rootRoute = routeMatch.path.replace('/:id/:matchStatus?', '');

  const handleClosePosition = useCallback(() => {
    closePositionRecord({
      positionId: Number(routeMatch.params.id),
      positionName: infoSections?.aboutPosition.name || '',
    });
    setCloseModalVisible(false);
  }, [closePositionRecord, routeMatch.params.id, infoSections]);

  useEffect(() => {
    fetchPositionRecordInfo(Number(routeMatch.params.id));
  }, [fetchPositionRecordInfo, routeMatch.params.id]);

  if (isLoadingInfo) return <InsightSpinner />;

  if (!infoSections) return null;

  const tabs: TabLayout['tabs'] = [
    {
      key: 'overview',
      name: (
        <FormattedMessage id={messageIds.vms.positionRecord.tabs.overview} />
      ),
      content: <PositionDetails />,
    },
    {
      key: 'notes',
      name: <FormattedMessage id={messageIds.vms.positionRecord.tabs.notes} />,
      content: <PositionNotes />,
      permission: AppPermissions.ViewPositionNotes,
    },
  ];

  return (
    <>
      {closeModalVisible && (
        <Modal
          okText={
            <FormattedMessage
              id={messageIds.vms.positionRecord.closePosition.okText}
            />
          }
          visible={closeModalVisible}
          onOk={handleClosePosition}
          cancelText={
            <FormattedMessage
              id={messageIds.vms.positionRecord.closePosition.cancelText}
            />
          }
          onCancel={() => setCloseModalVisible(false)}
        >
          <H1>
            <FormattedMessage
              id={messageIds.vms.positionRecord.closePosition.title}
            />
          </H1>
          <H4>
            <FormattedMessage
              id={messageIds.vms.positionRecord.closePosition.text}
            />
          </H4>
        </Modal>
      )}
      <Container background="pageSecondary" stretch ref={containerRef}>
        <Flex direction="column" gutter={2} gutterDirection="vertical" stretch>
          <FlexItem flex="0 1 auto">
            <PositionHeader
              isClosingPosition={isClosingPosition}
              isInsight={isInsight}
              infoSections={infoSections}
              handleClose={() => {
                setCloseModalVisible(true);
              }}
            />
          </FlexItem>
          <FlexItem flex={1}>
            <Switch>
              <Route
                path={[`${rootRoute}/:id/:matchStatus`]}
                render={({ match }: { match: Match<MatchParams> }) => {
                  if (MATCH_STATUSES[match.params.matchStatus]) {
                    return <MatchStatuses />;
                  }
                }}
              />
              <Route
                path={`${rootRoute}/:id`}
                render={() => (
                  <TabLayout tabs={tabs} routable stickTo={containerRef} />
                )}
              />
            </Switch>
          </FlexItem>
        </Flex>
      </Container>
    </>
  );
};

export default connect(
  (state: RootState) => ({
    isClosingPosition: selectors.selectPositionRecordState(state)
      .isClosingPosition,
    isLoadingInfo: selectors.selectPositionRecordState(state).isLoadingInfo,
    infoSections: selectors.selectPositionRecordState(state).infoSections,
  }),
  {
    closePositionRecord,
    fetchPositionRecordInfo,
    fetchMatchStatus,
  },
)(PositionRecord);
